import * as React from "react";
import { useEffect, useMemo, useState, useCallback } from "react";
import styled, { css } from "styled-components";
import Routes from "routing/Routes";
import { Link, Select } from "components/common";
import { Alert, Button, Row, Col, Input } from "components/bootstrap";
import useInputSetupWizard from "components/inputs/InputSetupWizard/hooks/useInputSetupWizard";
import useInputSetupWizardSteps from "components/inputs/InputSetupWizard/hooks/useInputSetupWizardSteps";
import useInputSetupWizardStepsHelper from "components/inputs/InputSetupWizard/hooks/useInputSetupWizardStepsHelper";
import { defaultCompare } from "logic/DefaultCompare";
import { INPUT_WIZARD_STEPS } from "components/inputs/InputSetupWizard/types";
import CreateStreamForm from "components/inputs/InputSetupWizard/steps/components/CreateStreamForm";
import useFilteredStreams from "components/inputs/InputSetupWizard/hooks/useFilteredStreams";
import usePipelinesConnectedStream from "hooks/usePipelinesConnectedStream";
import {
  StepWrapper,
  DescriptionCol,
  ButtonCol,
  StyledHeading,
  StyledList,
  RecommendedTooltip
} from "./components/StepWrapper";
import InputInUseAlert from "./components/InputInUseAlert";
const ExistingStreamCol = styled(Col)(
  ({ theme }) => css`
    padding-top: ${theme.spacings.sm};
    padding-bottom: ${theme.spacings.md};
  `
);
const CreateStreamCol = styled(Col)(
  ({ theme }) => css`
    padding-top: ${theme.spacings.sm};
    padding-bottom: ${theme.spacings.md};
    border-right: 1px solid ${theme.colors.cards.border};
  `
);
const StyledLabel = styled.label(
  ({ theme }) => css`
    font-weight: normal;
    line-height: 1.1;
    margin-bottom: ${theme.spacings.sm};
    display: inline-block;
    font-size: ${theme.fonts.size.h3};
    background: none;
  `
);
const StyledAlert = styled(Alert)`
  margin-top: 0;
`;
const SetupRoutingStep = () => {
  var _a, _b, _c;
  const [showSelectStream, setShowSelectStream] = useState(false);
  const [showCreateStream, setShowCreateStream] = useState(false);
  const currentStepName = useMemo(() => INPUT_WIZARD_STEPS.SETUP_ROUTING, []);
  const { goToPreviousStep, goToNextStep, orderedSteps, activeStep, wizardData } = useInputSetupWizard();
  const { stepsData, setStepsData } = useInputSetupWizardSteps();
  const { checkHasPreviousStep, checkHasNextStep, updateStepData, getStepData } = useInputSetupWizardStepsHelper();
  const newStream = getStepData(stepsData, currentStepName, "newStream");
  const selectedStreamId = getStepData(stepsData, currentStepName, "streamId");
  const isDefaultStream = getStepData(stepsData, currentStepName, "streamType") === "DEFAULT";
  const removeFromDefault = getStepData(stepsData, currentStepName, "removeMatchesFromDefault");
  const removeFromDefaultStreamChecked = typeof removeFromDefault === "undefined" ? true : removeFromDefault;
  const hasPreviousStep = checkHasPreviousStep(orderedSteps, activeStep);
  const hasNextStep = checkHasNextStep(orderedSteps, activeStep);
  const { data: streamsData, isLoading: isLoadingStreams } = useFilteredStreams();
  const streams = streamsData == null ? void 0 : streamsData.streams;
  const { data: streamPipelinesData } = usePipelinesConnectedStream(selectedStreamId, !!selectedStreamId);
  const defaultStepData = { streamType: "DEFAULT" };
  const isStepValid = useCallback(() => {
    const stepData = getStepData(stepsData, currentStepName);
    if (!stepData) return false;
    const { streamType, streamId } = stepData;
    if (showCreateStream && !newStream) return false;
    switch (streamType) {
      case "NEW":
        if (!newStream) return false;
        return true;
      case "EXISTING":
        if (!streamId) return false;
        return true;
      case "DEFAULT":
        return true;
      default:
        return false;
    }
  }, [currentStepName, newStream, showCreateStream, stepsData, getStepData]);
  useEffect(() => {
    var _a2;
    if (orderedSteps && activeStep && stepsData) {
      if (!((_a2 = getStepData(stepsData, currentStepName)) == null ? void 0 : _a2.streamType)) {
        const withInitialStepsData = updateStepData(stepsData, currentStepName, defaultStepData);
        setStepsData(withInitialStepsData);
      }
    }
  }, []);
  const options = useMemo(() => {
    if (!streams) return [];
    return streams.filter(({ is_default, is_editable }) => !is_default && is_editable).sort(({ title: key1 }, { title: key2 }) => defaultCompare(key1, key2)).map(({ title, id }) => ({ label: title, value: id }));
  }, [streams]);
  const handleStreamSelect = (streamId) => {
    if (streamId) {
      setStepsData(
        updateStepData(stepsData, currentStepName, {
          streamId,
          streamType: "EXISTING",
          removeMatchesFromDefault: removeFromDefaultStreamChecked
        })
      );
    } else {
      setStepsData(
        updateStepData(stepsData, currentStepName, {
          streamId: void 0,
          streamType: "DEFAULT",
          removeMatchesFromDefault: void 0
        })
      );
    }
  };
  const handleCreateStream = () => {
    updateStepData(stepsData, currentStepName, defaultStepData);
    setShowCreateStream(true);
  };
  const handleSelectStream = () => {
    setShowSelectStream(true);
  };
  const onNextStep = () => {
    goToNextStep();
  };
  const handleBackClick = () => {
    setStepsData(updateStepData(stepsData, currentStepName, defaultStepData, true));
    setShowSelectStream(false);
    setShowCreateStream(false);
    goToPreviousStep();
  };
  const handleCheckRemoveFromDefaultStream = () => {
    setStepsData(
      updateStepData(stepsData, currentStepName, {
        removeMatchesFromDefault: !removeFromDefaultStreamChecked
      })
    );
  };
  const streamHasConnectedPipelines = streamPipelinesData && (streamPipelinesData == null ? void 0 : streamPipelinesData.length) > 0;
  const submitStreamCreation = ({
    create_new_pipeline,
    ...stream
  }) => {
    setStepsData(
      updateStepData(stepsData, currentStepName, {
        newStream: stream,
        shouldCreateNewPipeline: create_new_pipeline != null ? create_new_pipeline : false,
        streamType: "NEW"
      })
    );
    onNextStep();
  };
  const showNewStreamSection = newStream || showCreateStream;
  const showSelectStreamSection = selectedStreamId || showSelectStream;
  return /* @__PURE__ */ React.createElement(StepWrapper, null, /* @__PURE__ */ React.createElement(InputInUseAlert, { inputId: (_a = wizardData == null ? void 0 : wizardData.input) == null ? void 0 : _a.id }), selectedStreamId && streamHasConnectedPipelines && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "warning", title: "Pipelines connected to target Stream" }, "We recommending checking the impact of these prior to completing the Input Setup. The target Stream has the following Pipelines connected to it:", /* @__PURE__ */ React.createElement(StyledList, null, streamPipelinesData.map((pipeline) => /* @__PURE__ */ React.createElement("li", { key: pipeline.title }, /* @__PURE__ */ React.createElement(Link, { to: Routes.SYSTEM.PIPELINES.PIPELINE(pipeline.id), target: "_blank" }, pipeline.title))))))), !showNewStreamSection && !showSelectStreamSection && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(DescriptionCol, { md: 12 }, /* @__PURE__ */ React.createElement(StyledList, null, /* @__PURE__ */ React.createElement("li", null, "Select a destination Stream to route messages from this input to."), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "We recommend creating a new stream for each new input."), " This will help categorise your messages into a basic schema."), /* @__PURE__ */ React.createElement("li", null, "Messages that are not routed to any Stream will be routed to the ", /* @__PURE__ */ React.createElement("strong", null, "Default Stream"), "."), /* @__PURE__ */ React.createElement("li", null, "Pipeline rules can be automatically created and attached to the ", /* @__PURE__ */ React.createElement("strong", null, "Default Stream"), " by this Wizard. These rules will be placed in the system managed Default Routing Pipeline, and will be automatically renamed (or deleted) to accurately reflect the state of this Input.")))), /* @__PURE__ */ React.createElement(Row, null, !selectedStreamId && /* @__PURE__ */ React.createElement(CreateStreamCol, { md: 6 }, /* @__PURE__ */ React.createElement(StyledHeading, null, "Route to a new Stream"), /* @__PURE__ */ React.createElement(RecommendedTooltip, { opened: true, withArrow: true, position: "bottom", label: "Recommended!" }, /* @__PURE__ */ React.createElement(Button, { onClick: handleCreateStream, bsStyle: "primary" }, "Create Stream"))), /* @__PURE__ */ React.createElement(ExistingStreamCol, { md: selectedStreamId ? 12 : 6 }, /* @__PURE__ */ React.createElement(StyledHeading, null, "Route to an existing Stream"), /* @__PURE__ */ React.createElement(Button, { onClick: handleSelectStream }, "Select Stream")))), showNewStreamSection && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(StyledHeading, null, "Create new Stream"), /* @__PURE__ */ React.createElement(
    CreateStreamForm,
    {
      submitForm: submitStreamCreation,
      handleBackClick,
      prevCreatedStream: (_b = stepsData == null ? void 0 : stepsData.SETUP_ROUTING) == null ? void 0 : _b.newStream,
      prevShouldCreateNewPipeline: (_c = stepsData == null ? void 0 : stepsData.SETUP_ROUTING) == null ? void 0 : _c.shouldCreateNewPipeline
    }
  ))), showSelectStreamSection && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(DescriptionCol, { md: 12 }, /* @__PURE__ */ React.createElement(StyledLabel, { htmlFor: "streams" }, "Choose an existing Stream"), /* @__PURE__ */ React.createElement(StyledList, null, /* @__PURE__ */ React.createElement("li", null, "Route messages from this input to an existing stream is selected."), /* @__PURE__ */ React.createElement("li", null, "Pipeline Rules will be created when the ", /* @__PURE__ */ React.createElement("strong", null, "Start Input"), " button is pressed.")))), !isLoadingStreams && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: "streams",
      onChange: handleStreamSelect,
      options,
      "aria-label": "Default Stream",
      clearable: true,
      placeholder: "Default Stream",
      value: selectedStreamId != null ? selectedStreamId : getStepData(stepsData, currentStepName, "streamId")
    }
  ), !isDefaultStream && /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "existing_remove_matches_from_default_stream",
      type: "checkbox",
      label: "Remove matches from \u2018Default Stream\u2019",
      title: "Remove matches from \u2018Default Stream\u2019",
      help: /* @__PURE__ */ React.createElement("span", null, "Don't assign messages that match this stream to the \u2018Default Stream\u2019."),
      name: "existing_remove_matches_from_default_stream",
      checked: removeFromDefaultStreamChecked,
      onChange: handleCheckRemoveFromDefaultStream
    }
  )))), ((hasPreviousStep || hasNextStep) && !showNewStreamSection || showSelectStreamSection) && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(ButtonCol, { md: 12 }, (hasPreviousStep || showSelectStreamSection) && /* @__PURE__ */ React.createElement(Button, { onClick: handleBackClick }, "Back"), hasNextStep && /* @__PURE__ */ React.createElement(Button, { disabled: !isStepValid(), onClick: onNextStep, bsStyle: "primary" }, "Next"))));
};
export default SetupRoutingStep;
